/**
 * ACTIVITY TOOLS — barra strumenti in fondo all'edit di un'attività.
 *
 * A SINISTRA il bottone "Commenti" (con conteggio): al click apre, SOTTO la
 * barra e a TUTTA LA RIGA, il composer (avatar utente + textarea) e la lista.
 * A DESTRA le "Associazioni" come tooltip/popover (AssociatedRecordsField
 * displayMode="click-to-edit": trigger + Popover a due colonne).
 *
 * I commenti sono persistiti via `commentApi`: creati con `addComment` al submit
 * e caricati con `getComments` all'apertura (fetch on-mount). Lo stato locale
 * `comments` è solo la cache di render della lista.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import { formatDateTime, htmlHasText, initialsFromName } from '@crm/shared';
import { useAppSelector } from '../store/hooks';
import { useCurrentGroupId } from '../auth/authSelectors';
import { userApi } from '../entities/user/userApi';
import { AssociatedRecordsField, type AssociationsPanelProps } from './AssociatedRecordsField';
import { RichTextEditor, type MentionItem } from './RichTextEditor';
import { fqnToSection } from './activitySections';
import DOMPurify from 'dompurify';
import { useToast } from './useToast';
import { useConfirmDialog } from './useConfirmDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { commentApi } from '../entities/comment/commentApi';
import type { Comment } from '../entities/comment/commentTypes';
import { resolvePortraitURL } from '../api/portrait';

/** Token menzione salvato nel body: [~screenName]. */
const MENTION_TOKEN = /\[~([^\]]+)\]/g;

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Converte il body salvato (con token menzione [~screenName]) in HTML pronto per
 * l'editor: ogni token diventa una chip menzione editabile
 * (<span class="mention" data-mention="screenName" contenteditable="false">@Nome</span>),
 * risolvendo il nome via la mappa screenName→fullName. È l'inverso di
 * RichTextEditor.serializeContent: al salvataggio le chip ridiventano [~screenName].
 */
function bodyToEditorHtml(body: string, nameByScreenName: Map<string, string>): string {
  return body.replace(MENTION_TOKEN, (full, screenName) => {
    const name = nameByScreenName.get(screenName) ?? screenName;
    return `<span class="mention" data-mention="${escapeText(screenName)}" contenteditable="false">@${escapeText(name)}</span>`;
  });
}

/**
 * Converte il body per la CITAZIONE nel composer (rispondi): i token menzione
 * [~screenName] diventano testo semplice @Nome — NON chip — così la citazione
 * non rimenziona gli utenti originali (niente notifiche doppie) al salvataggio
 * della risposta. La formattazione (tag) è preservata.
 */
function bodyToQuotedHtml(body: string, nameByScreenName: Map<string, string>): string {
  return body.replace(MENTION_TOKEN, (full, screenName) => {
    const name = nameByScreenName.get(screenName) ?? screenName;
    return `@${escapeText(name)}`;
  });
}

/**
 * Mappa screenName→fullName degli utenti del sito, per risolvere i token
 * menzione. Best-effort: in caso di errore o groupId mancante ritorna una mappa
 * vuota (i token restano come screenName, degradazione accettabile).
 */
async function resolveGroupUserNames(groupId: number): Promise<Map<string, string>> {
  if (!groupId) return new Map();
  try {
    const users = await userApi.getGroupUsers(groupId);
    return new Map(users.map((u) => [u.screenName, u.fullName]));
  } catch {
    return new Map();
  }
}

/**
 * Render del body di un commento: risolve i token menzione [~screenName] nel
 * nome completo dell'utente (@Nome, lato client via la lista utenti del sito,
 * cached) e sanifica l'HTML prima dell'inserimento (difesa anti stored-XSS).
 * Fino alla risoluzione mostra il body grezzo.
 */
function CommentBody({ body, groupId }: { body: string; groupId: number }) {
  const [html, setHtml] = useState(body);

  useEffect(() => {
    const screenNames = [...new Set([...body.matchAll(MENTION_TOKEN)].map((m) => m[1]))];
    if (screenNames.length === 0 || !groupId) {
      setHtml(body);
      return;
    }
    let cancelled = false;
    userApi
      .getGroupUsers(groupId)
      .then((users) => {
        if (cancelled) return;
        const nameByScreenName = new Map(users.map((u) => [u.screenName, u.fullName]));
        setHtml(
          body.replace(MENTION_TOKEN, (full, screenName) => {
            const name = nameByScreenName.get(screenName);
            return name ? `<span class="mention">@${escapeText(name)}</span>` : full;
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setHtml(body);
      });
    return () => {
      cancelled = true;
    };
  }, [body, groupId]);

  return (
    <div
      className="entity-comments__text"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

export function ActivityTools({
  hideAssociations = false,
  entityFqn,
  entityId,
  ...props
}: AssociationsPanelProps & {
  hideAssociations?: boolean;
  /** FQN + PK dell'entità (Note/Call/Meeting/...) a cui agganciare i commenti. */
  entityFqn: string;
  entityId: number;
}) {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);
  const groupId = useCurrentGroupId();
  const toast = useToast();
  const confirmDialog = useConfirmDialog();
  // Posizione corrente del FE: alimenta il link della mail di avviso menzione
  // (il backend antepone frontendURL). L'etichetta libera è il titolo pagina.
  const location = useLocation();
  const routeParams = useParams();
  // Deep-link commento: questa istanza è "il target" se la sua entità (classPK)
  // coincide con l'activityId nell'URL; commentId indica quale evidenziare.
  const focusCommentId = routeParams.commentId ? Number(routeParams.commentId) : null;
  const isFocusedActivity =
    routeParams.activityId != null && String(entityId) === routeParams.activityId;

  // sourcePath per la mail di menzione: /<kind>/<parentId>/<section>/<activityId>.
  // La section deriva dal TIPO dell'attività (entityFqn), non dal tab corrente,
  // così è corretta anche commentando dal tab "Tutte". Il backend appende
  // /comments/<commentId> (che non esiste ancora al momento del submit).
  const commentSource = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const base = segments.length >= 2 ? `/${segments[0]}/${segments[1]}` : location.pathname;
    const section = fqnToSection(entityFqn);
    return {
      sourcePath: section ? `${base}/${section}/${entityId}` : base,
      sourceLabel: document.title,
    };
  };

  // Ricerca utenti Liferay del sito per le menzioni @ nei commenti. Il token
  // salvato è lo screenName ([~screenName]); l'etichetta mostrata è il nome.
  const mentionSearch = useCallback(
    async (query: string): Promise<MentionItem[]> => {
      if (!groupId) return [];
      try {
        const users = await userApi.search(query, groupId);
        return users.slice(0, 8).map((u) => ({ value: u.screenName, label: u.fullName }));
      } catch {
        return [];
      }
    },
    [groupId],
  );

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState(''); // HTML del commento in composizione
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Incrementata al submit per rimontare l'editor (svuota il contentEditable).
  const [composerKey, setComposerKey] = useState(0);
  // HTML iniziale del composer (rispondi: citazione del commento). Vuoto =
  // composer normale.
  const [composerInitialHtml, setComposerInitialHtml] = useState('');
  // Edit inline di un commento esistente (owner). editDraft è in token-form
  // ([~screenName]), editInitialHtml in chip-form per pre-popolare l'editor.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editInitialHtml, setEditInitialHtml] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // Mappa userId -> portraitURL per gli avatar dei commenti (autori diversi
  // dall'utente corrente): il portraitURL viene dalla lista utenti del sito.
  const [avatarByUserId, setAvatarByUserId] = useState<Map<number, string>>(new Map());

  // Deep-link commento: stato/refs per evidenziare e scrollare al commento target.
  const [highlightedCommentId, setHighlightedCommentId] = useState<number | null>(null);
  const commentElRefs = useRef(new Map<number, HTMLElement | null>());
  // Id del commento già gestito: riparte se cambia (navigazione a un altro deep-link).
  const commentFocusHandledRef = useRef<number | null>(null);

  // Se questa attività è il target del deep-link, apro il pannello commenti.
  useEffect(() => {
    if (isFocusedActivity) setCommentsOpen(true);
  }, [isFocusedActivity]);

  // Pannello aperto + commenti caricati + <li> renderizzato: evidenzio e scrollo
  // al commento target. Gate su commentsOpen e sull'esistenza del ref così NON si
  // marca "fatto" prima che l'elemento esista (altrimenti lo scroll verrebbe perso).
  useEffect(() => {
    if (!isFocusedActivity || !commentsOpen || focusCommentId == null ||
      commentFocusHandledRef.current === focusCommentId) return;
    if (!comments.some((c) => c.commentId === focusCommentId)) return;
    const el = commentElRefs.current.get(focusCommentId);
    if (!el) return; // lista non ancora renderizzata: riprovo al prossimo render
    commentFocusHandledRef.current = focusCommentId;
    setHighlightedCommentId(focusCommentId);
    requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }, [isFocusedActivity, commentsOpen, focusCommentId, comments]);

  const avatarSrc = resolvePortraitURL(user?.portraitURL);
  const authorName = user?.fullName ?? '';
  const currentUserId = user?.userId;

  // Carica i commenti esistenti dell'entità all'apertura (e al cambio entità).
  // Ordinati per data decrescente, coerente col prepend dei nuovi commenti.
  useEffect(() => {
    if (!groupId || !entityFqn || !entityId) return;
    let cancelled = false;
    commentApi
      .getComments(groupId, entityFqn, entityId)
      .then((list) => {
        if (cancelled) return;
        setComments([...list].sort((a, b) => b.createDate.localeCompare(a.createDate)));
      })
      .catch((err) => {
        if (!cancelled)
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, entityFqn, entityId, toast]);

  // Avatar degli autori: dalla lista utenti del sito (cached) costruisce la
  // mappa userId -> portraitURL. Best-effort: in caso di errore gli avatar
  // ricadono sull'iniziale.
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    userApi
      .getGroupUsers(groupId)
      .then((users) => {
        if (cancelled) return;
        const map = new Map<number, string>();
        for (const u of users) {
          const url = resolvePortraitURL(u.portraitURL);
          if (url) map.set(u.id, url);
        }
        setAvatarByUserId(map);
      })
      .catch(() => {
        /* best-effort: nessun avatar immagine */
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  async function submitComment() {
    if (!htmlHasText(draft) || submitting || !groupId) return;
    setSubmitting(true);
    try {
      const created = await commentApi.addComment(groupId, {
        className: entityFqn,
        classPK: entityId,
        body: draft,
        ...commentSource(),
      });
      setComments((prev) => [created, ...prev]);
      setDraft('');
      setComposerInitialHtml('');
      setComposerKey((k) => k + 1);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  // Toggle del pannello commenti. Alla chiusura azzera l'eventuale citazione di
  // risposta e la bozza, così la riapertura mostra un composer pulito (il
  // contentEditable è comunque smontato/rimontato).
  function toggleComments() {
    if (commentsOpen) {
      setComposerInitialHtml('');
      setDraft('');
    }
    setCommentsOpen((o) => !o);
  }

  // Rispondi a un commento (disponibile su tutti, non solo i propri):
  // pre-valorizza il composer in alto col testo del commento citato (blockquote)
  // + una riga vuota per la replica. Le menzioni sono risolte a testo @Nome (non
  // rimenzionano gli originali). Apre il pannello commenti se chiuso.
  async function startReply(comment: Comment) {
    const nameByScreenName = await resolveGroupUserNames(groupId);
    const quoted = bodyToQuotedHtml(DOMPurify.sanitize(comment.body), nameByScreenName);
    const html = `<blockquote>${quoted}</blockquote><p></p>`;
    setComposerInitialHtml(html);
    setDraft(html);
    setCommentsOpen(true);
    setComposerKey((k) => k + 1);
  }

  async function handleDeleteComment(id: number) {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await commentApi.deleteComment(id);
      setComments((prev) => prev.filter((c) => c.commentId !== id));
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDeletingId(null);
    }
  }

  // Entra in edit su un commento (owner): risolve i token menzione in chip con
  // nome per l'editor. Se la fetch utenti fallisce, i token restano come
  // screenName (degradazione accettabile, l'edit resta possibile).
  async function startEdit(comment: Comment) {
    const nameByScreenName = await resolveGroupUserNames(groupId);
    // Normalizzo gli eventuali \n di wrap (commenti legacy salvati prima del fix
    // backend): così editor e payload del re-save restano su una riga, coerenti
    // con serializeContent. Sanifico il body (come CommentBody) PRIMA di
    // iniettarlo; le chip menzione controllate vengono aggiunte dopo.
    const cleanBody = comment.body.replace(/\r?\n/g, ' ');
    setEditInitialHtml(bodyToEditorHtml(DOMPurify.sanitize(cleanBody), nameByScreenName));
    setEditDraft(cleanBody);
    setEditingId(comment.commentId);
  }

  async function submitEdit(commentId: number) {
    if (!htmlHasText(editDraft) || editSaving) return;
    setEditSaving(true);
    try {
      const updated = await commentApi.updateComment(commentId, editDraft, commentSource());
      setComments((prev) => prev.map((c) => (c.commentId === commentId ? updated : c)));
      setEditingId(null);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setEditSaving(false);
    }
  }

  return (
    <div className="activity-tools">
      {/* Barra fissa: Commenti a sinistra, Associazioni (tooltip) a destra. */}
      <div className="activity-tools__tabs">
        <button
          type="button"
          className={`activity-tools__tab${commentsOpen ? ' is-active' : ''}`}
          onClick={toggleComments}
        >
          <ModeCommentOutlinedIcon fontSize="small" />
          <span>{t('comments.title')}</span>
          <span className="activity-tools__count">{comments.length}</span>
        </button>

        {!hideAssociations && (
          <AssociatedRecordsField
            displayMode="click-to-edit"
            className="activity-tools__associations"
            {...props}
          />
        )}
      </div>

      {/* Pannello commenti, a tutta riga, sotto la barra. */}
      {commentsOpen && (
        <div className="activity-tools__panel entity-comments">
          <div className="entity-comments__composer">
            <Avatar src={avatarSrc} alt={authorName} className="entity-comments__avatar">
              {initialsFromName(authorName)}
            </Avatar>
            <div className="entity-comments__composer-body">
              <RichTextEditor
                key={composerKey}
                autoFocus
                initialHtml={composerInitialHtml}
                placeholder={t('comments.placeholder')}
                onChange={setDraft}
                mentionSearch={mentionSearch}
              />
              <div className="entity-comments__composer-actions">
                <Button
                  size="small"
                  variant="contained"
                  onClick={submitComment}
                  disabled={!htmlHasText(draft) || submitting}
                >
                  {t('comments.submit')}
                </Button>
              </div>
            </div>
          </div>

          {comments.length > 0 && (
            <ul className="entity-comments__items">
              {comments.map((c) => (
                <li
                  key={c.commentId}
                  ref={(el) => { commentElRefs.current.set(c.commentId, el); }}
                  className={`entity-comments__item${c.commentId === highlightedCommentId ? ' entity-comments__item--highlight' : ''}`}
                >
                  <Avatar
                    src={avatarByUserId.get(c.userId) ?? (c.userId === currentUserId ? avatarSrc : undefined)}
                    alt={c.userName}
                    className="entity-comments__avatar entity-comments__avatar--sm"
                  >
                    {initialsFromName(c.userName)}
                  </Avatar>
                  <div className="entity-comments__item-body">
                    <div className="entity-comments__item-head">
                      <span className="entity-comments__author">{c.userName}</span>
                      <span className="entity-comments__date">{formatDateTime(c.createDate)}</span>
                      {editingId !== c.commentId && (
                        <>
                          <IconButton
                            size="small"
                            className="entity-comments__reply"
                            title={t('comments.reply')}
                            aria-label={t('comments.reply')}
                            onClick={() => startReply(c)}
                          >
                            <ReplyIcon fontSize="small" />
                          </IconButton>
                          {c.userId === currentUserId && (
                            <>
                              <IconButton
                                size="small"
                                className="entity-comments__edit"
                                title={t('common.edit')}
                                aria-label={t('common.edit')}
                                onClick={() => startEdit(c)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                className="entity-comments__delete"
                                title={t('common.delete')}
                                aria-label={t('common.delete')}
                                onClick={() =>
                                  confirmDialog.requestConfirm(t('comments.deleteConfirm'), () =>
                                    handleDeleteComment(c.commentId),
                                  )
                                }
                                disabled={deletingId === c.commentId}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    {editingId === c.commentId ? (
                      <div className="entity-comments__edit-form">
                        <RichTextEditor
                          key={`edit-${c.commentId}`}
                          autoFocus
                          initialHtml={editInitialHtml}
                          placeholder={t('comments.placeholder')}
                          onChange={setEditDraft}
                          mentionSearch={mentionSearch}
                        />
                        <div className="entity-comments__edit-actions">
                          <Button
                            size="small"
                            onClick={() => setEditingId(null)}
                            disabled={editSaving}
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => submitEdit(c.commentId)}
                            disabled={!htmlHasText(editDraft) || editSaving}
                          >
                            {t('common.save')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <CommentBody body={c.body} groupId={groupId} />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.confirm}
        onCancel={confirmDialog.cancel}
      />
    </div>
  );
}
